import React from "react";
import { useTranslation } from "react-i18next";
import { coveringData, deliveryDaysMatrix, zones } from "../constants/covering";

import ReactSvgMap from "../components/SvgMap/ReactSvgMap";
import { svgPathMap } from "../components/SvgMap/svgMapConstants";

const nodColorMap = {};
coveringData.forEach((item, index) => {
  const hue = Math.floor((360 / coveringData.length) * index);
  nodColorMap[item.nod] = `hsl(${hue}, 70%, 60%)`;
});

const countyCodeToNod = {};
coveringData.forEach((item) => {
  item.codes.forEach((code) => {
    countyCodeToNod[code] = item.nod;
  });
});

const coloredSvgPathMap = svgPathMap.map((item) => {
  const code = item.symbol;
  const nod = countyCodeToNod[code];
  return {
    ...item,
    pathColor: nod ? nodColorMap[nod] : "gray",
  };
});

function Covering() {
  const { t: translate } = useTranslation();

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center">
      {/* Hero Section */}
      <div
        className="relative w-full h-[60vh] flex items-center justify-center text-center bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/gallery-section.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60" />
        <h1 className="relative text-5xl font-bold text-white">
          {translate("covering_page_title")}
        </h1>
      </div>

      <div className="max-w-4xl bg-white shadow-md rounded-lg overflow-hidden my-10 mx-2 sm:mx-6">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-900 text-white text-sm uppercase tracking-wider">
              <th className="px-6 py-3">NOD ATS</th>
              <th className="px-6 py-3">Zona</th>
              <th className="px-6 py-3">Arie deservita</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {coveringData.map((item, index) => (
              <tr key={index} className="hover:bg-gray-100">
                <td className="px-6 py-4 font-medium text-gray-900">
                  {item.nod}
                </td>
                <td className="px-6 py-4 text-gray-600">{item.zona}</td>
                <td className="px-6 py-4 text-gray-500">{item.arie}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReactSvgMap
        withHiddenMargins={true}
        svgPathData={coloredSvgPathMap}
        containerClassName="mb-2"
        coveringData={coveringData}
        modalContentComponent={(selectedPath) => {
          if (!selectedPath?.symbol) return null;

          const nodInfo = coveringData.find((entry) =>
            entry.codes.includes(selectedPath.symbol)
          );

          if (!nodInfo) return null;

          return (
            <div>
              <h2 className="text-lg font-bold mb-2">{nodInfo.nod}</h2>

              <p className="text-sm text-gray-800">
                <span className="font-semibold">Acoperire:</span> {nodInfo.arie}
              </p>
              {nodInfo.contact && (
                <p className="text-sm text-gray-800 mt-1">
                  <span className="font-semibold">Contact:</span>{" "}
                  {nodInfo.contact}
                </p>
              )}
            </div>
          );
        }}
      />

      <div className="w-full max-w-5xl bg-white shadow-md rounded-lg overflow-hidden my-10">
        <h2 className="p-2.5 font-semibold text-center">
          {translate("covering_page_table_title")}
        </h2>

        <table className="w-full border-collapse text-center shadow-md">
          <thead>
            <tr>
              <th className="bg-gray-900 text-white py-2 px-3">ZONE</th>
              {zones.map((zone) => (
                <th key={zone} className="bg-gray-900 text-white py-2 px-3">
                  {zone}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zones.map((rowZone) => (
              <tr key={rowZone} className="even:bg-gray-50">
                <th className="bg-gray-100 text-gray-900 font-medium py-2 px-3">
                  {rowZone}
                </th>
                {zones.map((colZone) => {
                  const value = deliveryDaysMatrix[rowZone]?.[colZone] || "";
                  const isDiagonal = rowZone === colZone;

                  return (
                    <td
                      key={`${rowZone}-${colZone}`}
                      className={`py-2 px-3 border ${
                        isDiagonal
                          ? "bg-gray-200 font-semibold text-gray-800"
                          : ""
                      }`}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Covering;
